import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkle,
  ShieldCheck,
  Database,
  Bell,
  Lightning,
  Users,
  Buildings,
  ClipboardText,
  ChartLineUp,
  CheckCircle,
  Target,
  ListChecks,
  CloudArrowUp,
  LockKey,
  Briefcase,
  Wrench,
  Mountains,
  GraduationCap,
  Calendar,
  Receipt,
  Stack,
  CurrencyDollar,
  Globe,
  Plugs,
  MagicWand,
  Coins,
  Files,
  ChartPieSlice,
  ShieldCheckered,
  ArrowsClockwise,
  Eye,
  Vault,
  ScanSmiley,
  HandCoins,
} from "@phosphor-icons/react";

// =================================================================
// Propuesta Técnica + Económica · DoSoft × GORE Antofagasta
// Sistema de Gestión y Control de Inversiones y Proyectos GORE
// A4 portrait: 794×1123 (96 DPI)
// =================================================================

const PAGE_W = 794;
const PAGE_H = 1123;

function Page({ id, children, bg = "default" }) {
  const bgClass = {
    default: "bg-white text-slate-900",
    dark: "bg-gradient-to-br from-slate-950 via-[#0d1526] to-slate-950 text-white",
    orange: "bg-gradient-to-br from-brand-orange-600 via-brand-orange-500 to-amber-600 text-white",
  }[bg];
  return (
    <div
      id={id}
      className={`relative overflow-hidden shadow-2xl ${bgClass}`}
      style={{ width: PAGE_W, height: PAGE_H }}
    >
      {children}
    </div>
  );
}

// Marca DoSoft — logo image
function DoSoftLogo({ dark = false, size = "h-9" }) {
  return (
    <img
      src="/assets/dosoft-logo.webp"
      alt="DoSoft — Ingeniería de Software"
      className={`${size} w-auto`}
      style={dark ? { filter: "brightness(0) invert(1)" } : undefined}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
}

function PageFrame({ id, title, num, total, bg = "default", children }) {
  const textColor = bg === "default" ? "text-slate-900" : "text-white";
  const subtle = bg === "default" ? "text-slate-500" : "text-white/60";
  const borderCol = bg === "default" ? "border-slate-200" : "border-white/10";
  return (
    <Page id={id} bg={bg}>
      <div className={`absolute top-0 left-0 right-0 h-[72px] px-12 flex items-center justify-between z-20 border-b ${borderCol}`}>
        <DoSoftLogo dark={bg !== "default"} size="h-9" />
        <div className={`text-right ${subtle}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold">{title}</div>
          {num && total && <div className="text-[10px] mt-0.5">{num} / {total}</div>}
        </div>
      </div>
      <div className={`absolute bottom-0 left-0 right-0 h-[60px] px-12 flex items-center justify-between z-20 border-t ${borderCol}`}>
        <div className={`text-[10px] uppercase tracking-[0.25em] font-semibold ${subtle}`}>
          Propuesta GORE Antofagasta · Julio 2026
        </div>
        <div className={`text-[10px] uppercase tracking-[0.25em] font-semibold ${subtle}`}>
          dosoft.cl
        </div>
      </div>
      <div className={`absolute inset-0 pt-[88px] pb-[76px] px-12 ${textColor}`}>{children}</div>
    </Page>
  );
}

function SectionLabel({ children, accent = "orange" }) {
  const tones = {
    orange: "border-brand-orange-500/40 bg-brand-orange-500/10 text-brand-orange-600",
    green: "border-brand-green-500/40 bg-brand-green-500/10 text-brand-green-600",
    white: "border-white/40 bg-white/15 text-white",
  };
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border ${tones[accent]} px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-bold`}>
      <Sparkle size={10} weight="fill" /> {children}
    </span>
  );
}

// =================================================================
// 01 — Portada
// =================================================================
function PageCover() {
  return (
    <Page id="cover" bg="dark">
      <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-brand-orange-500/30 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 w-[600px] h-[600px] rounded-full bg-sky-500/15 blur-3xl" />

      <div className="relative h-full flex flex-col justify-between px-12 py-12">
        <div>
          <DoSoftLogo dark size="h-14" />
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-[11px] uppercase tracking-[0.3em] text-brand-orange-300 font-bold rounded-full border border-brand-orange-400/40 bg-brand-orange-500/10 px-3 py-1">
              Spin-off de Legav
            </span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-brand-green-300 font-bold rounded-full border border-brand-green-400/40 bg-brand-green-500/10 px-3 py-1">
              Locales · Antofagasta
            </span>
          </div>
          <div className="mt-8">
            <SectionLabel accent="white">Propuesta confidencial · Julio 2026</SectionLabel>
          </div>
          <h1 className="font-display text-[54px] leading-[0.98] font-bold mt-6 tracking-tight">
            Sistema de<br />Gestión y<br /><span className="text-brand-orange-400">Control de<br />Inversiones</span>.
          </h1>
          <p className="text-[16px] text-slate-300 mt-5 leading-snug max-w-[620px]">
            Plataforma para el seguimiento de proyectos y financiamiento del Gobierno Regional,
            incluyendo recursos entregados a privados.
          </p>
        </div>

        <div>
          <div className="rounded-2xl border-2 border-brand-orange-400/40 bg-brand-orange-500/10 p-6 backdrop-blur-sm">
            <div className="text-[11px] uppercase tracking-[0.25em] text-brand-orange-300 font-bold">Preparada para</div>
            <div className="font-display text-3xl font-bold mt-2">GORE Antofagasta</div>
            <div className="text-[14px] text-slate-300 mt-1">Gobierno Regional de Antofagasta</div>

            <div className="mt-4 pt-4 border-t border-white/15">
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange-300 font-bold">Atención de</div>
              <div className="font-display text-xl font-bold mt-1">Pablo Rojas</div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-white/10 px-3 py-2.5">
                <div className="text-[9px] uppercase tracking-wider text-white/60">Modalidad</div>
                <div className="font-display text-lg font-bold mt-1">Hecho a medida</div>
              </div>
              <div className="rounded-lg bg-white/10 px-3 py-2.5">
                <div className="text-[9px] uppercase tracking-wider text-white/60">Producto</div>
                <div className="font-display text-lg font-bold mt-1">Nuevo · SaaS</div>
              </div>
              <div className="rounded-lg bg-white/10 px-3 py-2.5">
                <div className="text-[9px] uppercase tracking-wider text-white/60">Origen</div>
                <div className="font-display text-lg font-bold mt-1">Antofagasta</div>
              </div>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-14 rounded-full bg-brand-orange-400" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-orange-300">DoSoft</div>
                <div className="font-display text-2xl font-bold tracking-wide text-white">Spin-off de Legav</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Documento</div>
              <div className="font-display text-base font-bold">PPT + PPE</div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

// =================================================================
// 02 — Resumen Ejecutivo
// =================================================================
function PageResumen() {
  return (
    <PageFrame id="resumen" title="Resumen Ejecutivo" num="02" total="10">
      <SectionLabel>Resumen Ejecutivo</SectionLabel>
      <h2 className="font-display text-[34px] leading-[1.05] font-bold mt-4 max-w-[700px]">
        Sistema hecho a medida para el <span className="text-brand-orange-600">control de inversiones</span> del GORE.
      </h2>
      <p className="text-[14px] text-slate-600 mt-4 leading-relaxed max-w-[700px]">
        DoSoft — <em>spin-off de Legav</em> — diseñará e implementará un sistema nuevo,
        exclusivo para el Gobierno Regional de Antofagasta, orientado a la gestión y control
        integral de proyectos y financiamientos, con foco especial en el <strong>seguimiento
        de recursos entregados a privados</strong>.
      </p>

      <div className="grid grid-cols-3 gap-3 mt-6">
        {[
          { icon: HandCoins, t: "Control financiero", d: "Presupuestos, ejecución, rendiciones y trazabilidad peso a peso." },
          { icon: ClipboardText, t: "Control operativo", d: "Hitos, avances, evidencias y responsables por proyecto." },
          { icon: MagicWand, t: "Hecho a medida", d: "Módulos, flujos y campos ajustados a los procesos del GORE." },
        ].map((c) => (
          <div key={c.t} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="w-10 h-10 rounded-lg bg-brand-orange-500/15 border border-brand-orange-500/30 flex items-center justify-center mb-2">
              <c.icon size={20} weight="duotone" className="text-brand-orange-600" />
            </div>
            <div className="font-display text-lg font-bold text-slate-900">{c.t}</div>
            <div className="text-[11px] text-slate-600 mt-1 leading-relaxed">{c.d}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border-2 border-brand-orange-500/30 bg-gradient-to-br from-brand-orange-500/10 to-transparent p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange-600 font-bold">Inversión — Plan Piloto</div>
        <div className="grid grid-cols-2 gap-5 mt-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Desarrollo</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-1">$10.000.000</div>
            <div className="text-[11px] text-slate-600 mt-1">Pago único · no incluye IVA</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Mensualidad operacional</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-1">$2.000.000</div>
            <div className="text-[11px] text-slate-600 mt-1">CLP mensual · no incluye IVA</div>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-xl border-l-4 border-brand-green-500 bg-brand-green-50 px-4 py-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-green-700 font-bold">Módulos adicionales</div>
        <p className="text-[12px] text-slate-700 mt-1 leading-relaxed">
          En caso de requerir módulos adicionales al alcance base, se cotizan y se agregan
          al presupuesto según complejidad y volumen.
        </p>
      </div>

      <p className="text-[11px] text-slate-500 italic mt-4 max-w-[700px]">
        Esta propuesta consolida la <strong>Propuesta Técnica (PPT)</strong> y la
        <strong> Propuesta Económica (PPE)</strong>, con vigencia de 30 días desde su emisión.
      </p>
    </PageFrame>
  );
}

// =================================================================
// 03 — El Problema
// =================================================================
function PageProblema() {
  return (
    <PageFrame id="problema" title="El Problema" num="03" total="10">
      <SectionLabel>El problema a resolver</SectionLabel>
      <h2 className="font-display text-[34px] leading-[1.05] font-bold mt-4 max-w-[700px]">
        Los recursos entregados <span className="text-rose-500">deben estar a la vista</span>.
      </h2>
      <p className="text-[14px] text-slate-600 mt-4 leading-relaxed max-w-[700px]">
        El Gobierno Regional financia decenas de proyectos ejecutados por privados
        (organizaciones, fundaciones, cooperativas, empresas). Sin un sistema dedicado,
        el seguimiento vive en planillas, correos y carpetas — con impacto directo en
        transparencia, control ciudadano y ejecución presupuestaria.
      </p>

      <div className="grid grid-cols-2 gap-4 mt-6">
        {[
          { t: "Rendiciones fuera de plazo", d: "Los privados demoran o no rinden. Sin alertas, el GORE se entera tarde." },
          { t: "Ejecución sin control real", d: "Sin evidencia visible, es imposible saber si los recursos se están usando bien." },
          { t: "Duplicidad de información", d: "Los mismos datos se piden 3 veces a distintos privados. Nadie sabe cuál es la versión vigente." },
          { t: "Auditoría sin trazabilidad", d: "La Contraloría pide antecedentes y el equipo reconstruye a mano." },
        ].map((c) => (
          <div key={c.t} className="rounded-xl border border-rose-200 bg-rose-50/50 p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-rose-100 border border-rose-200 flex items-center justify-center flex-shrink-0">
                <Target size={18} weight="duotone" className="text-rose-600" />
              </div>
              <div>
                <div className="font-display text-base font-bold text-slate-900">{c.t}</div>
                <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">{c.d}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="absolute bottom-[90px] left-12 right-12">
        <div className="rounded-xl border-l-4 border-brand-orange-500 bg-brand-orange-50 px-5 py-3">
          <p className="text-[12px] text-slate-700 leading-relaxed">
            <strong className="text-brand-orange-700">Costo oculto:</strong> lo que no se registra
            no se rinde. Y lo que no se rinde afecta transparencia, credibilidad ante la
            Contraloría y ejecución presupuestaria futura.
          </p>
        </div>
      </div>
    </PageFrame>
  );
}

// =================================================================
// 04 — Metodología (5 pasos)
// =================================================================
function PageMetodologia() {
  const steps = [
    { n: "1", t: "Diagnóstico", d: "Reunión con el equipo GORE para mapear flujos actuales, tipos de proyectos y recursos entregados a privados.", icon: ClipboardText, dur: "Día 1-2", start: 0, end: 2, color: "bg-brand-orange-500" },
    { n: "2", t: "Diseño a medida", d: "Definición de módulos, campos, permisos, ciclo de vida del proyecto y flujo de rendiciones.", icon: MagicWand, dur: "Día 3-5", start: 2, end: 5, color: "bg-brand-green-500" },
    { n: "3", t: "Desarrollo + integración", d: "Construcción del sistema y evaluación de integraciones con otros sistemas del GORE.", icon: Wrench, dur: "Día 6-10", start: 5, end: 10, color: "bg-sky-500" },
    { n: "4", t: "Capacitación", d: "Entrenamiento al equipo GORE y a los ejecutores privados que rendirán en la plataforma.", icon: GraduationCap, dur: "Día 11-13", start: 10, end: 13, color: "bg-violet-500" },
    { n: "5", t: "Go-live + acompañamiento", d: "Salida en producción, soporte intensivo y validación final del usuario designado.", icon: Lightning, dur: "Día 14-15", start: 13, end: 15, color: "bg-rose-500" },
  ];
  return (
    <PageFrame id="metodologia" title="Metodología" num="04" total="10">
      <SectionLabel>Metodología de trabajo</SectionLabel>
      <h2 className="font-display text-[34px] leading-[1.05] font-bold mt-4 max-w-[700px]">
        Cinco pasos, en <span className="text-brand-orange-600">15 días</span>.
      </h2>
      <p className="text-[14px] text-slate-600 mt-4 leading-relaxed max-w-[700px]">
        Un proceso llave en mano. El equipo del GORE opera el sistema en producción a los 15 días de iniciado el proyecto.
      </p>

      {/* Carta Gantt */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">Carta Gantt · 15 días</div>
          <div className="text-[10px] text-slate-400 font-mono">D1 → D15</div>
        </div>
        <div className="flex items-center gap-1 ml-[140px] mb-2">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="flex-1 text-center text-[9px] font-mono text-slate-400 font-semibold">{i + 1}</div>
          ))}
        </div>
        <div className="space-y-1.5">
          {steps.map((s) => (
            <div key={s.n} className="flex items-center gap-3">
              <div className="w-[130px] text-[11px] font-semibold text-slate-700 truncate flex items-center gap-1.5">
                <span className="w-4 h-4 rounded-full bg-brand-orange-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">{s.n}</span>
                {s.t}
              </div>
              <div className="flex-1 relative h-5 bg-slate-100 rounded-md overflow-hidden">
                <div
                  className={`absolute top-0 bottom-0 ${s.color} rounded-md flex items-center px-2 text-[8px] font-bold text-white`}
                  style={{
                    left: `${(s.start / 15) * 100}%`,
                    width: `${((s.end - s.start) / 15) * 100}%`,
                  }}
                >
                  {s.dur}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {steps.map((s) => (
          <div key={s.n} className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-full bg-brand-orange-500 text-white font-display font-bold flex items-center justify-center text-xs flex-shrink-0">{s.n}</div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="font-display text-[13px] font-bold text-slate-900">{s.t}</div>
                <div className="text-[9px] uppercase tracking-wider text-brand-orange-600 font-bold">{s.dur}</div>
              </div>
              <div className="text-[11px] text-slate-600 leading-snug">{s.d}</div>
            </div>
          </div>
        ))}
      </div>
    </PageFrame>
  );
}

// =================================================================
// 05 — Entregables
// =================================================================
function PageEntregables() {
  const items = [
    { icon: Database, t: "Sistema hecho a medida", d: "Plataforma nueva, exclusiva para el GORE Antofagasta." },
    { icon: Briefcase, t: "Módulo de proyectos financiados", d: "Ficha completa con eje, montos, plazos y ejecutor privado." },
    { icon: HandCoins, t: "Control financiero", d: "Presupuesto asignado vs ejecutado, con detalle por ítem." },
    { icon: ChartLineUp, t: "Control operativo", d: "Hitos, avances físicos y curva S por proyecto." },
    { icon: Files, t: "Rendiciones y evidencias", d: "Carga de rendiciones, respaldos y validación digital." },
    { icon: Users, t: "Portal para ejecutores privados (opcional)", d: "Acceso limitado para que los privados carguen su ejecución. Módulo adicional." },
    { icon: Bell, t: "Alertas automáticas", d: "Avisos de rendiciones fuera de plazo y desvíos presupuestarios." },
    { icon: ChartPieSlice, t: "Dashboard ejecutivo", d: "Vista consolidada para autoridades: cartera, ejecución, riesgos." },
    { icon: ShieldCheckered, t: "Trazabilidad para Contraloría", d: "Cada acción con autor, fecha, evidencia y bitácora inmutable." },
    { icon: GraduationCap, t: "Capacitación + manuales", d: "Sesiones para el equipo GORE y los ejecutores privados." },
  ];
  return (
    <PageFrame id="entregables" title="Entregables" num="05" total="10">
      <SectionLabel>Resultados esperados</SectionLabel>
      <h2 className="font-display text-[34px] leading-[1.05] font-bold mt-4 max-w-[700px]">
        10 entregables concretos al cierre del piloto.
      </h2>
      <p className="text-[14px] text-slate-600 mt-4 leading-relaxed max-w-[700px]">
        Cada entregable es verificable, con un responsable asignado y aprobado por el usuario designado por el GORE.
      </p>

      <div className="grid grid-cols-2 gap-3 mt-6">
        {items.map((it, i) => (
          <div key={it.t} className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-brand-green-500/15 border border-brand-green-500/30 flex items-center justify-center flex-shrink-0">
              <it.icon size={18} weight="duotone" className="text-brand-green-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-slate-400 font-bold">{String(i + 1).padStart(2, "0")}</span>
                <div className="font-display text-[13px] font-bold text-slate-900 leading-tight">{it.t}</div>
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">{it.d}</div>
            </div>
          </div>
        ))}
      </div>
    </PageFrame>
  );
}

// =================================================================
// 06 — Alcance funcional
// =================================================================
function PageAlcance() {
  const groups = [
    {
      title: "Control Financiero",
      icon: HandCoins,
      color: "orange",
      items: [
        "Presupuesto por proyecto y por ítem",
        "Ejecución peso a peso, con fecha y respaldo",
        "Rendiciones de gastos por el ejecutor privado",
        "Alertas por desviación presupuestaria",
        "Reporte financiero consolidado",
      ],
    },
    {
      title: "Control Operativo",
      icon: ClipboardText,
      color: "sky",
      items: [
        "Hitos y entregables con responsable",
        "Avance físico (%) por hito",
        "Curva S del proyecto",
        "Evidencias fotográficas y documentales",
        "Bitácora de terreno",
      ],
    },
    {
      title: "Recursos entregados a privados",
      icon: HandCoins,
      color: "green",
      items: [
        "Ficha por ejecutor privado (empresas, ONGs, cooperativas)",
        "Convenios y contratos digitalizados",
        "Portal de acceso restringido para privados (opcional)",
        "Trazabilidad de cada transferencia",
        "Cumplimiento de plazos de rendición",
      ],
    },
    {
      title: "Transparencia y Auditoría",
      icon: Eye,
      color: "violet",
      items: [
        "Bitácora inmutable con autor y fecha",
        "Reportes para Contraloría en un click",
        "Backups automáticos 24/7",
        "Roles y permisos granulares",
        "Alineado con la Ley 21.719 de Chile",
      ],
    },
  ];
  const toneMap = {
    orange: { border: "border-brand-orange-500/30", bg: "from-brand-orange-500/10", icon: "text-brand-orange-600", iconBg: "bg-brand-orange-500/15 border-brand-orange-500/30" },
    sky: { border: "border-sky-500/30", bg: "from-sky-500/10", icon: "text-sky-600", iconBg: "bg-sky-500/15 border-sky-500/30" },
    green: { border: "border-brand-green-500/30", bg: "from-brand-green-500/10", icon: "text-brand-green-700", iconBg: "bg-brand-green-500/15 border-brand-green-500/30" },
    violet: { border: "border-violet-500/30", bg: "from-violet-500/10", icon: "text-violet-600", iconBg: "bg-violet-500/15 border-violet-500/30" },
  };
  return (
    <PageFrame id="alcance" title="Alcance funcional" num="06" total="10">
      <SectionLabel>Alcance funcional</SectionLabel>
      <h2 className="font-display text-[32px] leading-[1.05] font-bold mt-4 max-w-[700px]">
        Qué incluye el sistema base.
      </h2>
      <p className="text-[13px] text-slate-600 mt-3 leading-relaxed max-w-[700px]">
        Cuatro ejes cubiertos por el sistema piloto. Módulos adicionales se cotizan según requerimiento.
      </p>

      <div className="grid grid-cols-2 gap-3 mt-5">
        {groups.map((g) => {
          const t = toneMap[g.color];
          return (
            <div key={g.title} className={`rounded-xl border ${t.border} bg-gradient-to-br ${t.bg} to-transparent p-4`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg border ${t.iconBg} flex items-center justify-center`}>
                  <g.icon size={20} weight="duotone" className={t.icon} />
                </div>
                <div className="font-display text-base font-bold text-slate-900">{g.title}</div>
              </div>
              <ul className="space-y-1.5">
                {g.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-[11px] text-slate-700 leading-snug">
                    <CheckCircle size={13} weight="duotone" className={`${t.icon} mt-0.5 flex-shrink-0`} />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </PageFrame>
  );
}

// =================================================================
// 08 — Plan Piloto (PPE)
// =================================================================
function PagePlanPiloto() {
  return (
    <PageFrame id="plan-piloto" title="Plan Piloto · Propuesta Económica" num="07" total="10">
      <SectionLabel>Propuesta económica</SectionLabel>
      <h2 className="font-display text-[34px] leading-[1.05] font-bold mt-4 max-w-[700px]">
        Plan <span className="text-brand-orange-600">Piloto GORE</span>.
      </h2>
      <p className="text-[14px] text-slate-600 mt-3 leading-relaxed max-w-[700px]">
        Sistema nuevo, diseñado y desarrollado a la medida del Gobierno Regional de Antofagasta.
        El equipo GORE opera y carga su propia información en la plataforma.
      </p>

      <div className="mt-5 rounded-2xl border-2 border-brand-orange-500/40 bg-gradient-to-br from-brand-orange-50 to-white p-5 shadow-lg">
        <div className="grid grid-cols-2 gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange-600 font-bold">Desarrollo</div>
            <div className="font-display text-[46px] font-bold text-slate-900 leading-none mt-2">
              $10.000.000
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">Pago único · sin IVA</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange-600 font-bold">Mensualidad operacional</div>
            <div className="font-display text-[46px] font-bold text-slate-900 leading-none mt-2">
              $2.000.000
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">CLP mensual · sin IVA</div>
          </div>
        </div>

        <div className="mt-5 space-y-2 border-t border-slate-200 pt-4">
          {[
            { t: "Diseño a medida y desarrollo del sistema", v: "Incluido" },
            { t: "Módulos base (financiero, operativo, transparencia)", v: "Incluido" },
            { t: "Capacitación al equipo GORE", v: "Incluido" },
            { t: "Operación y soporte técnico 24/7", v: "Mensualidad" },
            { t: "Actualizaciones y mejoras continuas", v: "Mensualidad" },
          ].map((r) => (
            <div key={r.t} className="flex items-center justify-between text-[13px]">
              <div className="text-slate-800">{r.t}</div>
              <div className="text-brand-green-700 font-bold">{r.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 rounded-xl border-l-4 border-brand-green-500 bg-brand-green-50 px-4 py-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-green-700 font-bold">Módulos adicionales</div>
        <p className="text-[12px] text-slate-700 mt-1 leading-relaxed">
          Si el GORE requiere módulos fuera del alcance base, se cotizan y se suman al presupuesto
          según complejidad y volumen de datos. Detalle en la siguiente página.
        </p>
      </div>
    </PageFrame>
  );
}

// =================================================================
// 09 — Módulos adicionales
// =================================================================
function PageModulosAdicionales() {
  const mods = [
    { icon: Plugs, t: "Integraciones con otros sistemas", d: "Conexión con plataformas municipales y bases regionales." },
    { icon: ChartLineUp, t: "Business Intelligence avanzado", d: "Dashboard con analítica y tableros para dirección superior." },
    { icon: Users, t: "Portal para ejecutores privados", d: "Acceso restringido para que los privados carguen su ejecución y rendiciones." },
    { icon: ScanSmiley, t: "Portal público de transparencia (opcional a cotizar)", d: "Vista pública para que la ciudadanía consulte el estado de los proyectos." },
    { icon: Bell, t: "Alertas multicanal", d: "WhatsApp, correo y SMS para responsables internos y ejecutores privados." },
  ];
  return (
    <PageFrame id="modulos" title="Módulos adicionales" num="08" total="10">
      <SectionLabel accent="green">Módulos adicionales</SectionLabel>
      <h2 className="font-display text-[32px] leading-[1.05] font-bold mt-4 max-w-[700px]">
        Se agregan al piloto <span className="text-brand-green-600">según necesidad</span>.
      </h2>
      <p className="text-[13px] text-slate-600 mt-3 leading-relaxed max-w-[700px]">
        Cada módulo se cotiza individualmente en base al alcance funcional acordado con el GORE.
        Los valores dependen de complejidad, volumen y cantidad de integraciones.
      </p>

      <div className="grid grid-cols-2 gap-3 mt-5">
        {mods.map((m) => (
          <div key={m.t} className="rounded-xl border border-brand-green-500/30 bg-gradient-to-br from-brand-green-50/50 to-white p-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-brand-green-500/15 border border-brand-green-500/30 flex items-center justify-center flex-shrink-0">
                <m.icon size={22} weight="duotone" className="text-brand-green-700" />
              </div>
              <div>
                <div className="font-display text-base font-bold text-slate-900">{m.t}</div>
                <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">{m.d}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-xl border-l-4 border-brand-orange-500 bg-brand-orange-50 px-4 py-3">
        <p className="text-[12px] text-slate-700 leading-relaxed">
          <strong className="text-brand-orange-700">Cotización sobre pedido:</strong> DoSoft entrega
          una PPE específica por cada módulo adicional que el GORE decida incorporar, con su valor
          de desarrollo y su ajuste de mensualidad.
        </p>
      </div>
    </PageFrame>
  );
}

// =================================================================
// 10 — Términos
// =================================================================
function PageTerminos() {
  return (
    <PageFrame id="terminos" title="Términos técnicos y comerciales" num="09" total="10">
      <SectionLabel>Términos técnicos y comerciales</SectionLabel>
      <h2 className="font-display text-[28px] leading-[1.05] font-bold mt-4 max-w-[700px]">
        Detalles de la Propuesta Técnica.
      </h2>

      <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-5">
        {[
          { icon: MagicWand, t: "Hecho a medida", d: "Módulos, campos y flujos ajustados al GORE. Sin costo en cambios contemplados por el alcance base." },
          { icon: Plugs, t: "Integrable", d: "Evaluación de conexión con bases de datos y aplicaciones ya existentes." },
          { icon: GraduationCap, t: "Acompañamiento integral", d: "Desarrollo, capacitación y soporte durante todo el ciclo." },
          { icon: Lightning, t: "Soporte técnico 24/7", d: "Monitoreo continuo de la plataforma." },
          { icon: LockKey, t: "Seguridad de datos", d: "Cifrado de extremo a extremo, backups 24/7 en cloud." },
          { icon: ShieldCheck, t: "Ley 21.719 de Chile", d: "Alineado con la nueva Ley de Protección de Datos Personales." },
          { icon: ClipboardText, t: "Confidencialidad", d: "Aceptamos cláusulas de confidencialidad y compliance que tenga el GORE." },
          { icon: Users, t: "Carga de datos", d: "El equipo del GORE y los ejecutores privados cargan la información en la plataforma." },
          { icon: CheckCircle, t: "Validación final", d: "Requiere aprobación del usuario creador o designado por el mandante GORE." },
          { icon: Receipt, t: "Condiciones comerciales", d: "Valores en CLP, no incluyen IVA. Vigencia: 30 días desde su emisión." },
        ].map((it) => (
          <div key={it.t} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
              <it.icon size={18} weight="duotone" className="text-brand-orange-600" />
            </div>
            <div>
              <div className="font-display text-[13px] font-bold text-slate-900 leading-tight">{it.t}</div>
              <div className="text-[11px] text-slate-600 mt-1 leading-snug">{it.d}</div>
            </div>
          </div>
        ))}
      </div>
    </PageFrame>
  );
}

// =================================================================
// 11 — Cierre
// =================================================================
function PageCierre() {
  return (
    <Page id="cierre" bg="orange">
      <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-white/20 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 w-[600px] h-[600px] rounded-full bg-amber-300/30 blur-3xl" />

      <div className="relative h-full flex flex-col justify-between px-12 py-12">
        <div>
          <SectionLabel accent="white">Cierre · Próximos pasos</SectionLabel>
          <h2 className="font-display text-[52px] leading-[0.95] font-bold mt-6 tracking-tight">
            Listos para acompañar<br />al <span className="underline decoration-white/40 decoration-[6px] underline-offset-8">GORE</span>.
          </h2>
          <p className="text-[15px] text-white/95 mt-6 leading-snug max-w-[660px]">
            Esta propuesta consolida la Propuesta Técnica (PPT) y la Propuesta Económica (PPE).
            Quedamos disponibles para resolver dudas o alcances y dar inicio al plan piloto.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="rounded-2xl border-2 border-white/30 bg-white/10 backdrop-blur-sm p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-bold">Plan Piloto</div>
              <div className="font-display text-2xl font-bold mt-2">$10.000.000 + $2.000.000</div>
              <div className="text-[11px] text-white/85 mt-1">Desarrollo + mensual · sin IVA</div>
            </div>
            <div className="rounded-2xl border-2 border-white bg-white/15 backdrop-blur-sm p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white font-bold">Módulos adicionales</div>
              <div className="font-display text-2xl font-bold mt-2">Cotización a pedido</div>
              <div className="text-[11px] text-white/85 mt-1">Se suman al piloto según necesidad</div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-end justify-between">
            <DoSoftLogo dark size="h-16" />
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/80">Documento</div>
              <div className="font-display text-2xl font-bold mt-1">PPT + PPE</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-white/70 font-semibold">
            <span>Antofagasta · Chile</span>
            <span>Documento confidencial · Julio 2026</span>
          </div>
        </div>
      </div>
    </Page>
  );
}

// =================================================================
// Main page
// =================================================================
const PAGES = [
  { Component: PageCover, title: "01 · Portada" },
  { Component: PageResumen, title: "02 · Resumen Ejecutivo" },
  { Component: PageProblema, title: "03 · El Problema" },
  { Component: PageMetodologia, title: "04 · Metodología (5 pasos)" },
  { Component: PageEntregables, title: "05 · Entregables" },
  { Component: PageAlcance, title: "06 · Alcance Funcional" },
  { Component: PagePlanPiloto, title: "07 · Plan Piloto" },
  { Component: PageModulosAdicionales, title: "08 · Módulos Adicionales" },
  { Component: PageTerminos, title: "09 · Términos" },
  { Component: PageCierre, title: "10 · Cierre" },
];

export default function PpGore() {
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 print-page">
      <header className="bg-white border-b border-slate-200 shadow-sm print:hidden">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold">Propuesta DoSoft · GORE Antofagasta</h1>
            <p className="text-sm text-slate-500">Sistema de Gestión y Control de Inversiones · 10 páginas A4 · listo para PDF</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              className="rounded-full bg-brand-orange-500 hover:bg-brand-orange-600 text-white font-bold px-5 py-2.5 text-sm transition shadow-lg shadow-brand-orange-500/30"
              data-testid="pp-gore-print-btn"
            >
              Imprimir / Guardar PDF
            </button>
            <Link
              to="/pp-fcab"
              className="text-sm text-slate-600 hover:text-slate-900 transition"
            >
              ← Propuesta FCAB
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 py-10 space-y-10 print:space-y-0 print:p-0 print:max-w-none">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 print:hidden">
          <strong className="text-slate-900 font-bold">Documento propuesta — DoSoft (spin-off de Legav)</strong>
          <p className="mt-2">10 páginas A4 portrait con la Propuesta Técnica y Económica del <em>Sistema de Gestión y Control de Inversiones y Proyectos GORE</em>. Usa el botón <em>Imprimir / Guardar PDF</em> para descargar como archivo.</p>
        </div>

        {PAGES.map(({ Component, title }, i) => (
          <section key={title} className="print:break-after-page print:m-0">
            <div className="flex items-center justify-between mb-3 print:hidden">
              <h3 className="font-display text-base font-bold text-slate-800">{title}</h3>
              <span className="text-xs uppercase tracking-wider text-slate-400">{i + 1} / {PAGES.length}</span>
            </div>
            <div className="flex justify-center">
              <div
                className="pp-page-scaler"
                style={{ width: PAGE_W * 0.85, height: PAGE_H * 0.85, overflow: "hidden" }}
                data-testid={`pp-gore-page-${i + 1}`}
              >
                <div className="pp-page-inner" style={{ transform: "scale(0.85)", transformOrigin: "top left" }}>
                  <Component />
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 0; }
          body, html { background: white !important; margin: 0; padding: 0; }
          .print-page { background: white !important; }
          .pp-page-scaler {
            width: auto !important;
            height: auto !important;
            overflow: visible !important;
          }
          .pp-page-inner {
            transform: none !important;
          }
        }
        .font-display { font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif; }
      `}</style>
    </div>
  );
}
